import {
  GQLVulnerability,
  GQLVulnerabilityApplication,
  VulnerabilitySeverity,
  PolicyViolation,
  PendingStatus,
  Session
} from '../types/vulnerability'
import { DateTime } from '../types/date-time-type'
import { VulnerabilityStatus } from '../shared'
import { ApplicationImportance } from '../types/application-importance'
import { GQLBugTracker } from '../types/bug-trackers'

const subtractDate = (date, n: number) => {
  date.setDate(date.getDate() - n)
  return date
}
const datesRelativeToNow = {
  now: new Date(),
  yesterday: subtractDate(new Date(), 1),
  sevenDaysAgo: subtractDate(new Date(), 7),
  thirtyDaysAgo: subtractDate(new Date(), 30)
}

interface BuildMockVulnProps {
  readonly title?: string
  readonly ruleName?: string
  readonly visible?: boolean
  readonly application?: GQLVulnerabilityApplication
  readonly severity?: VulnerabilitySeverity
  readonly defaultSeverity?: VulnerabilitySeverity
  readonly tags?: string[]
  readonly lastDetected?: DateTime
  readonly firstDetected?: DateTime
  readonly status?: VulnerabilityStatus
  readonly substatus?: string
  readonly reportedToBugTracker?: boolean
  readonly reportedToBugTrackerTime?: DateTime
  readonly bugTrackers?: GQLBugTracker[]
  readonly violations?: PolicyViolation[]
  readonly sessions?: Session[]
  readonly pendingStatus?: PendingStatus
}

let appId = 0
const appIdFactory = () => ++appId + ''

let policyViolationId = 0
const policyViolationIdFactory = () => ++policyViolationId + ''

function buildMockVulnerability({
  title = 'Mock Vuln Title',
  ruleName = 'Mock Rule Name',
  visible = true,
  severity = 'NOTE' as VulnerabilitySeverity,
  defaultSeverity = 'NOTE' as VulnerabilitySeverity,
  application,
  tags = [],
  firstDetected = datesRelativeToNow.yesterday.toISOString(),
  lastDetected = datesRelativeToNow.now.toISOString(),
  status = 'Reported' as VulnerabilityStatus,
  substatus,
  reportedToBugTracker = false,
  reportedToBugTrackerTime,
  bugTrackers = [],
  violations = [],
  sessions = [],
  pendingStatus
}: BuildMockVulnProps): GQLVulnerability {
  return {
    id: 'id',
    title,
    ruleName,
    visible,
    severity,
    defaultSeverity,
    application: application || buildMockVulnerabilityApp({}),
    tags,
    firstDetected,
    lastDetected,
    status,
    substatus: substatus || null,
    reportedToBugTracker,
    reportedToBugTrackerTime: reportedToBugTrackerTime || null,
    bugTrackers,
    violations,
    sessions,
    pendingStatus: pendingStatus || null
  }
}

function buildMockVulnerabilityApp({
  name = 'WebGoat',
  url = 'http://localhost:8080/WebGoat',
  importance = 'CRITICAL'
}: {
  readonly name?: string
  readonly url?: string
  readonly importance?: ApplicationImportance
}): GQLVulnerabilityApplication {
  return {
    id: appIdFactory(),
    name,
    url,
    importance,
    licensed: true,
    language: 'Java',
    role: 'ADMIN'
  }
}

function buildMockPolicyViolations(n: number) {
  const objs: PolicyViolation[] = []
  for (let i = 0; i < n; i++) {
    objs.push(buildMockPolicyViolation())
  }
  return objs
}

function buildMockPolicyViolation() {
  const id = policyViolationIdFactory()
  return {
    id,
    name: `Policy Violation ${id}`
  }
}

export const mockVulnerabilities = [
  buildMockVulnerability({
    title: 'Vuln w/Pending Status',
    status: 'Reported',
    pendingStatus: {
      status: 'Fixed',
      created: new Date().toDateString()
    }
  }),
  buildMockVulnerability({
    title: 'Forms Without Autocomplete Prevention on 2 pages',
    lastDetected: datesRelativeToNow.thirtyDaysAgo.toISOString()
  }),
  buildMockVulnerability({
    title: 'Anti-Caching Controls Missing on 1 page',
    tags: ['here are', 'a couple of', 'tags'],
    lastDetected: datesRelativeToNow.yesterday.toISOString()
  }),
  buildMockVulnerability({
    severity: 'LOW',
    title: 'Overly Long Session Timeout in /WEB-INF/web.xml',
    lastDetected: datesRelativeToNow.sevenDaysAgo.toISOString()
  }),
  buildMockVulnerability({
    severity: 'MEDIUM',
    title: "'SHA' hash algorithm used at HttpOnly.java",
    lastDetected: datesRelativeToNow.thirtyDaysAgo.toISOString()
  }),
  buildMockVulnerability({
    severity: 'MEDIUM',
    title: 'Insecure JSP Placement',
    lastDetected: datesRelativeToNow.thirtyDaysAgo.toISOString()
  }),
  buildMockVulnerability({
    severity: 'HIGH',
    title:
      'Cross-Site Scripting from "Password" Parameter, "Username" Parameter on "/WebGoat/attack" page (lesson_content.jsp, line 37)',
    lastDetected: datesRelativeToNow.thirtyDaysAgo.toISOString()
  }),
  buildMockVulnerability({
    severity: 'CRITICAL',
    title:
      'SQL Injection from "Password" Parameter, "Username" Parameter on "/WebGoat/attack" page',
    lastDetected: datesRelativeToNow.thirtyDaysAgo.toISOString()
  }),
  buildMockVulnerability({
    status: 'Reported',
    reportedToBugTracker: true,
    bugTrackers: [
      {
        name: 'Test',
        type: 'JIRA',
        creation: datesRelativeToNow.thirtyDaysAgo.toISOString(),
        ticketKey: 'FAKEBUG-764',
        ticketTitle:
          'CONTRAST: Forms Without Autocomplete Prevention on 2 pages',
        ticketUrl: 'https://contrast.atlassian.net/browse/FAKEBUG-764'
      }
    ]
  }),
  buildMockVulnerability({
    status: 'Reported',
    reportedToBugTracker: true,
    bugTrackers: [
      {
        name: 'Test A',
        type: 'VSTS',
        creation: datesRelativeToNow.thirtyDaysAgo.toISOString(),
        ticketKey: 'FAKEBUG-764',
        ticketTitle:
          'CONTRAST: Forms Without Autocomplete Prevention on 2 pages',
        ticketUrl: 'https://contrast.atlassian.net/browse/FAKEBUG-764'
      },
      {
        name: 'Test B',
        type: 'JIRA',
        creation: datesRelativeToNow.thirtyDaysAgo.toISOString(),
        ticketKey: 'FAKEBUG-764',
        ticketTitle:
          'CONTRAST: Forms Without Autocomplete Prevention on 2 pages',
        ticketUrl: 'https://contrast.atlassian.net/browse/FAKEBUG-764'
      }
    ]
  }),
  buildMockVulnerability({
    severity: 'NOTE',
    title: 'mock',
    ruleName: 'Version Header Enabled',
    visible: false
  }),
  buildMockVulnerability({
    title: 'One app servers'
  }),
  buildMockVulnerability({
    title: 'Two app servers'
  }),
  buildMockVulnerability({
    title: 'A lot of app servers'
  }),
  buildMockVulnerability({
    title: 'With One Violation',
    violations: buildMockPolicyViolations(1)
  }),
  buildMockVulnerability({
    title: 'With Multiple Violations',
    violations: buildMockPolicyViolations(10)
  })
]
